/**
 * Place Autocomplete (New) helpers.
 * Replaces deprecated google.maps.places.Autocomplete with PlaceAutocompleteElement
 * while preserving a legacy PlaceResult shape for handlePlaceResult / existing callers.
 */

import { useEffect, useRef } from 'react'
import { ensurePlacesLibrary, useGoogleMapsApi } from './googleMaps'

/** Map Autocomplete options.types → PlaceAutocompleteElement includedPrimaryTypes. */
export function mapAutocompleteTypes(types) {
  if (!types?.length) return undefined
  // Legacy "(cities)" etc. are not 1:1; geocode is the common HouseFolios case.
  return types
    .map((t) => (t === '(cities)' ? 'locality' : t))
    .filter(Boolean)
}

/**
 * Convert a Places API (New) Place into a legacy Autocomplete PlaceResult-like object.
 */
export function placeToLegacyResult(place) {
  if (!place) return null

  const address_components = (place.addressComponents || []).map(
    (component) => ({
      long_name: component.longText || '',
      short_name: component.shortText || '',
      types: component.types || [],
    }),
  )

  const loc = place.location
  let lat
  let lng
  if (loc) {
    lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat
    lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng
  }

  return {
    address_components,
    formatted_address: place.formattedAddress || place.displayName || '',
    name: place.displayName || '',
    geometry:
      lat != null && lng != null
        ? {
            location: {
              lat: () => lat,
              lng: () => lng,
            },
          }
        : undefined,
  }
}

const PLACE_FIELDS = [
  'addressComponents',
  'location',
  'formattedAddress',
  'displayName',
]

/**
 * Mount PlaceAutocompleteElement next to a legacy <input>, hide the input,
 * and fire onPlaceChanged with a legacy-compatible PlaceResult.
 *
 * @returns cleanup function
 */
export function bindPlaceAutocomplete(inputEl, options = {}) {
  const {
    types = ['geocode'],
    includedRegionCodes,
    onPlaceChanged,
    placeholder,
  } = options

  if (!inputEl || typeof window === 'undefined') {
    return () => {}
  }

  const parent = inputEl.parentNode
  if (!parent) return () => {}

  const host = document.createElement('div')
  host.setAttribute('data-hf-place-host', '')
  host.style.width = '100%'
  host.style.display = 'block'

  const previousDisplay = inputEl.style.display
  inputEl.style.display = 'none'
  parent.insertBefore(host, inputEl)

  let autocompleteEl = null
  let cancelled = false
  let selectHandler = null

  const setup = async () => {
    try {
      await ensurePlacesLibrary()
    } catch (err) {
      console.error('[Places] ensurePlacesLibrary failed', err)
      if (!cancelled) {
        inputEl.style.display = previousDisplay
        host.remove()
      }
      return
    }
    if (cancelled) return

    const PlaceAutocompleteElement =
      window.google?.maps?.places?.PlaceAutocompleteElement
    if (!PlaceAutocompleteElement) {
      console.error('[Places] PlaceAutocompleteElement unavailable')
      inputEl.style.display = previousDisplay
      host.remove()
      return
    }

    const elementOptions = {}
    const primaryTypes = mapAutocompleteTypes(types)
    if (primaryTypes?.length) {
      elementOptions.includedPrimaryTypes = primaryTypes
    }
    if (includedRegionCodes?.length) {
      elementOptions.includedRegionCodes = includedRegionCodes
    }

    autocompleteEl = new PlaceAutocompleteElement(elementOptions)
    autocompleteEl.style.width = '100%'
    const ph =
      placeholder || inputEl.getAttribute('placeholder') || inputEl.placeholder
    if (ph) {
      try {
        autocompleteEl.placeholder = ph
      } catch {
        /* some builds expose placeholder via attribute */
        autocompleteEl.setAttribute('placeholder', ph)
      }
    }
    host.appendChild(autocompleteEl)

    selectHandler = async (event) => {
      try {
        const prediction = event.placePrediction
        if (!prediction?.toPlace) return
        const place = prediction.toPlace()
        await place.fetchFields({ fields: PLACE_FIELDS  })
        const legacy = placeToLegacyResult(place)
        if (!legacy?.address_components?.length) {
          onPlaceChanged?.(null)
          return
        }
        if (legacy.formatted_address) {
          inputEl.value = legacy.formatted_address
          inputEl.dispatchEvent(new Event('input', { bubbles: true  }))
          inputEl.dispatchEvent(new Event('change', { bubbles: true  }))
        }
        onPlaceChanged?.(legacy)
      } catch (err) {
        console.error('[Places] gmp-select handler failed', err)
        onPlaceChanged?.(null)
      }
    }

    autocompleteEl.addEventListener('gmp-select', selectHandler)
  }

  setup()

  return () => {
    cancelled = true
    if (autocompleteEl && selectHandler) {
      autocompleteEl.removeEventListener('gmp-select', selectHandler)
    }
    autocompleteEl?.remove()
    host.remove()
    inputEl.style.display = previousDisplay
  }
}

/**
 * Convenience: bind by element id once the Maps JS API is loaded.
 */
export function bindPlaceAutocompleteById(elementId, options = {}) {
  const inputEl = document.getElementById(elementId)
  if (!inputEl) {
    console.warn(`[Places] input #${elementId} not found`)
    return () => {}
  }
  return bindPlaceAutocomplete(inputEl, options)
}

/**
 * Wait for an input to appear in the DOM (e.g. MUI Dialog mount), then bind.
 * Returns a cleanup function safe to call from useEffect.
 */
export function bindPlaceAutocompleteWhenReady(elementId, options = {}) {
  let cleanup = () => {}
  let cancelled = false
  let rafId = 0

  const tryBind = () => {
    if (cancelled) return
    const inputEl = document.getElementById(elementId)
    if (!inputEl) {
      rafId = requestAnimationFrame(tryBind)
      return
    }
    cleanup = bindPlaceAutocomplete(inputEl, options)
  }

  tryBind()

  return () => {
    cancelled = true
    if (rafId) cancelAnimationFrame(rafId)
    cleanup()
  }
}

/**
 * React entry point for Places fields: shares useGoogleMapsApi with map UIs,
 * then mounts PlaceAutocompleteElement when the target input is ready.
 *
 * @param {string} elementId
 * @param {object} options — same as bindPlaceAutocomplete (types, onPlaceChanged, …)
 * @param {boolean} [enabled=true] — gate on dialog open, etc.
 */
export function usePlaceAutocomplete(elementId, options = {}, enabled = true) {
  const { isLoaded, loadError } = useGoogleMapsApi()
  const optionsRef = useRef(options)
  optionsRef.current = options

  useEffect(() => {
    if (!enabled || !isLoaded || !elementId) return undefined
    return bindPlaceAutocompleteWhenReady(elementId, {
      types: optionsRef.current.types,
      includedRegionCodes: optionsRef.current.includedRegionCodes,
      placeholder: optionsRef.current.placeholder,
      onPlaceChanged: (place) => optionsRef.current.onPlaceChanged?.(place),
    })
  }, [enabled, isLoaded, elementId])

  return { isLoaded, loadError }
}
